import styles from './EntityPicker.module.scss';

import { Stack } from '@mui/material';
import Typography from '@mui/material/Typography';
import clsx from 'clsx';
import { useMemo } from 'react';

import { InboundDeliveryNote } from '@work4all/models/lib/Classes/InboundDeliveryNote.entity';
import { SortDirection } from '@work4all/models/lib/DataProvider';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { Tooltip } from '../tooltip/Tooltip';

import { ERPColumnSelector } from './components/erp-column-selector/ERPColumnSelector';
import { ListEntityPicker } from './components/list-entity-picker/ListEntityPicker';
import { usePickerSettings } from './hooks/use-picker-settings';
import { IPickerProps } from './types';

export type InboundDeliveryNotePickerProps<TMultiple extends boolean> =
  IPickerProps<InboundDeliveryNote, TMultiple>;

export function InboundDeliveryNotePicker<TMultiple extends boolean>(
  props: InboundDeliveryNotePickerProps<TMultiple>
) {
  const { data, ...rest } = props;

  const allFields = useMemo(() => {
    return { ...INBOUND_DELIVERY_NOTE_DATA, ...data };
  }, [data]);

  const { visiblePickerColumnsSetting: inboundDeliveryNotesColumns } =
    usePickerSettings(Entities.inboundDeliveryNote);

  const visibleInboundDeliveryNotesColumns = inboundDeliveryNotesColumns.filter(
    (column) => column.isVisible
  )?.length;
  const gridTemplateColumns =
    visibleInboundDeliveryNotesColumns > 2 ? '2fr 1fr 4fr' : '3fr 1fr 2fr';

  const renderItem = (item: InboundDeliveryNote) => {
    const label = item.note;

    return (
      <Stack
        direction="row"
        display="grid"
        gridTemplateColumns={gridTemplateColumns}
        className={clsx({ [styles.greyedStrikeThrough]: item.isClosed })}
        style={{ width: '100%' }}
      >
        <Tooltip title={label} showOnlyOnOverflow enterNextDelay={1000}>
          <Typography variant="body2" noWrap flex="3">
            {label}
          </Typography>
        </Tooltip>
        <Typography
          variant="body2"
          noWrap
          className={styles.secondaryInfo}
          flex="1"
        >
          {item.number}
        </Typography>
        <ERPColumnSelector
          item={item}
          columnSettings={inboundDeliveryNotesColumns}
        />
      </Stack>
    );
  };

  return (
    <ListEntityPicker
      layout="advanced"
      entity={Entities.inboundDeliveryNote}
      data={allFields}
      filterBy={['number', 'note']}
      sortBy="date"
      sortByDirection={SortDirection.DESCENDING}
      {...rest}
      renderItemContent={renderItem}
      renderChipContent={(el: InboundDeliveryNote) => el.note}
    />
  );
}

const INBOUND_DELIVERY_NOTE_DATA: InboundDeliveryNote<EMode.query> = {
  id: null,
  date: null,
  number: null,
  note: null,
  isClosed: null,
  project: {
    id: null,
    name: null,
    number: null,
  },
  businessPartnerContactCombined: {
    businessPartner: {
      id: null,
      data: {
        supplier: {
          id: null,
          name: null,
          number: null,
        },
        customer: {
          id: null,
          name: null,
          number: null,
        },
      },
    },
  },
};
